import React, { Component, ErrorInfo, ReactNode, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Global Error Boundary to catch React component rendering errors.
 */
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(_: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught React component error:', error, errorInfo);
    toast.error(`Application Error: ${error.message || 'Something went wrong during rendering'}`, {
      duration: 6000,
    });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6 text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
          <p className="text-gray-600 mb-6">The application encountered an unexpected error. We've been notified and are looking into it.</p>
          <button
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Global Error Listener for non-React errors (Promises, Network, Scripts).
 */
const GlobalErrorListeners = () => {
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.warn('Unhandled promise rejection:', event.reason);
      
      // Don't toast if it's an RTK Query error (already handled by middleware)
      // RTK Query rejections usually have a 'status' or 'data' or 'error' property
      const reason = event.reason;
      if (reason && (reason.status || reason.data || actionTypeIsRTK(reason))) {
        return;
      }

      const message = reason?.message || 'A background process failed';
      toast.error(`Background Task Error: ${message}`);
    };

    const handleWindowError = (event: ErrorEvent) => {
      // Ignore React's resize-observer loop limit exceeded (common and usually harmless)
      if (event.message === 'ResizeObserver loop limit exceeded') return;
      
      console.error('Window error captured:', event.error);
      toast.error(`Script Error: ${event.message || 'An unexpected error occurred'}`);
    };

    // Helper to check if it's an RTK error to avoid double toast
    const actionTypeIsRTK = (reason: any) => {
        return reason && typeof reason === 'object' && ('status' in reason || 'data' in reason);
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleWindowError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleWindowError);
    };
  }, []);

  return null;
};

/**
 * Unified Global Error Handler that combines Error Boundary and Event Listeners.
 */
export const GlobalErrorHandler: React.FC<Props> = ({ children }) => {
  return (
    <ErrorBoundary>
      <GlobalErrorListeners />
      {children}
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: '#333',
            color: '#fff',
            borderRadius: '8px',
            fontSize: '14px',
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </ErrorBoundary>
  );
};
